import './BubbleTypeahead.scss';

import { notUndefined } from '@go-shared/lib/functional';
import { hasOwnProperty, hashStringToColor } from '@go-shared/lib/utils';
import { capitalize } from '@go-shared/lib/string';
import Fuse from 'fuse.js';
import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import uuid from 'uuid';

import { BubbleBadge, BubbleInput } from '.';
import { BubbleBadgeProps } from './BubbleBadge';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export interface BubbleTypeaheadOption<T> {
  key: string;
  item: T;
  variant?: BubbleBadgeProps['variant'];
  readonly?: boolean;
}

export type BubbleTypeaheadValue<T> = BubbleTypeaheadOption<T> & {
  refKey: string;
};

interface BubbleTypeaheadPropsBase<T> {
  variant?: 'navy' | 'forest' | 'velvet' | 'titanium';
  value?: Array<BubbleTypeaheadValue<T>>;
  options: Array<BubbleTypeaheadOption<T>>;
  block?: boolean;
  allowNew?: boolean;
  allowDuplicates?: boolean;
  alwaysShowSuggestions?: boolean;
  noSuggestionsClick?: boolean;
  placeholder?: string;
  suggestionsText?: string;
  maxItems?: number;
  inputRef?: React.RefObject<HTMLInputElement>;
  appearance?: 'bare' | 'rounded';
  inputClassName?: string;
  inputStyle?: React.CSSProperties;
  menuClassName?: string;
  suggestionClassName?: string;
  readOnly?: boolean;
  labelExtractor?: (item: T) => string;
  colorExtractor?: (item: T) => string;
  suggestionsRenderFn?: (label: string, option: BubbleTypeaheadOption<T>) => React.ReactNode;
  onCreate?: (name: string) => void;
  onAdd?: (value: BubbleTypeaheadValue<T>) => void;
  onRemove?: (value: BubbleTypeaheadValue<T>) => void;
  onTrash?: (option: BubbleTypeaheadOption<T>) => void;
  onClearAll?: () => void;
  readOnlyLabel?: string;
}

export type BubbleTypeaheadProps<T> = React.HTMLAttributes<HTMLDivElement> &
  BubbleTypeaheadPropsBase<T>;

function BubbleTypeahead<T extends string | number | object>(props: BubbleTypeaheadProps<T>) {
  const {
    // from component
    variant,
    value,
    options,
    block,
    allowNew,
    allowDuplicates,
    alwaysShowSuggestions,
    noSuggestionsClick: noAutoFocus,
    maxItems,
    placeholder,
    suggestionsText,
    appearance,
    suggestionsRenderFn,
    inputRef: propInputRef,
    inputClassName: propInputClassName,
    inputStyle: propInputStyle,
    menuClassName: propMenuClassName,
    suggestionClassName: propSuggestionClassName,
    labelExtractor,
    colorExtractor,
    onAdd,
    onCreate,
    onRemove,
    onTrash,
    onClearAll,
    readOnly,
    // from div element itself
    className: propClassName,
    style: propStyle,
    onClick,
    readOnlyLabel,
    ...rest
  } = props;

  const [search, setSearch] = useState('');
  const trimmedSearch = search.trim();
  const [showSuggestions, setShowSuggestions] = useState(false);

  const typeaheadRef = useRef<HTMLDivElement | null>(null);
  const itemsRef = useRef<HTMLInputElement | null>(null);
  const backupInputRef = useRef<HTMLInputElement | null>(null);
  const inputRef = propInputRef || backupInputRef;

  const [inputHasFocus, setInputHasFocus] = useState(false);
  const [badgeHasFocus, setBadgeHasFocus] = useState(false);
  const [itemsHasFocus, setItemsHasFocus] = useState(false);

  function calculateFocusStates() {
    setInputHasFocus(inputRef.current ? document.activeElement === inputRef.current : false);
    setBadgeHasFocus(
      Array.prototype.findIndex.call(
        typeaheadRef.current?.getElementsByClassName('b-tab') || [],
        el => el && document.activeElement === el
      ) > -1
    );
    setItemsHasFocus(
      Array.prototype.findIndex.call(
        typeaheadRef.current?.getElementsByClassName('s-tab') || [],
        el => el && document.activeElement === el
      ) > -1
    );
  }

  useEffect(() => {
    setShowSuggestions(inputHasFocus || itemsHasFocus || badgeHasFocus);
  }, [inputHasFocus, itemsHasFocus, badgeHasFocus, setShowSuggestions]);

  const fuse = useMemo(() => {
    return new Fuse(
      options.map(opt => ({
        text: resolveLabel(opt),
        option: opt,
      })),
      {
        ...DEFAULT_FUSE_SETTINGS,
        keys: ['text'],
        shouldSort: true,
        includeScore: true,
      }
    );
  }, [options]);

  const fuseSearchResults = useMemo(() => {
    if (!trimmedSearch) return undefined;

    const fuseSearch = fuse.search(trimmedSearch);

    return {
      hasPerfectMatch: fuseSearch.findIndex(fs => (fs.score || 1) <= 0.00000000001) >= 0,
      options: fuseSearch.map(s => s.item.option),
    };
  }, [fuse, search]);

  const internalOptions = useMemo(() => {
    let opts = fuseSearchResults || { hasPerfectMatch: false, options };

    if (!allowDuplicates && value) {
      opts.options = opts.options.filter(o => !value.find(v => v.refKey === o.key));
    }

    if (maxItems) opts.options = opts.options.splice(0, maxItems);

    return opts;
  }, [fuseSearchResults, options, value]);

  const internalValue = !value
    ? []
    : value
        .map(val => {
          const opt = options.find(o => o.key === val.refKey);

          return opt
            ? {
                value: val,
                option: opt,
              }
            : undefined;
        })
        .filter(notUndefined);

  const showClearAllBadge = onClearAll && internalValue.length > 0;
  const totalBadgesAmt = internalValue.length + (showClearAllBadge ? 1 : 0);

  useEffect(() => {
    if (showSuggestions) {
      document.addEventListener('click', documentClickHandler);
    } else {
      setSearch('');
      document.removeEventListener('click', documentClickHandler);
    }
  }, [showSuggestions]);

  function resolveLabel(opt: BubbleTypeaheadOption<T> | BubbleTypeaheadValue<T>) {
    return labelExtractor
      ? labelExtractor(opt.item)
      : capitalize('refKey' in opt ? opt.refKey : opt.key);
  }

  function onAddItem(intOpt: BubbleTypeaheadOption<T>) {
    if (onAdd) {
      setSearch('');
      onAdd({ key: uuid(), refKey: intOpt.key, item: intOpt.item });
    }

    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  }

  function onCreateItem() {
    if (onCreate) {
      setSearch('');
      onCreate(trimmedSearch);
    }

    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  }

  function onTrashItem(intOpt: BubbleTypeaheadOption<T>) {
    if (onTrash) {
      onTrash(intOpt);
    }

    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  }

  function onRemoveItem(intVal: BubbleTypeaheadValue<T>) {
    if (onRemove) {
      onRemove(intVal);
    }

    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  }

  function onClearItems() {
    if (onClearAll) {
      onClearAll();
    }

    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  }

  const documentClickHandler = useCallback(
    (event: MouseEvent) => {
      // check to see if click is outside the element for closing dropdown
      if (
        typeaheadRef.current &&
        event.target &&
        event.target instanceof Node &&
        !typeaheadRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    },
    [typeaheadRef, setShowSuggestions]
  );

  function focusVertical(div: HTMLElement, start: number, end: number, inc: (i: number) => number) {
    let focusNext: 'item' | 'action' | 'elem' | undefined = undefined;

    let i = start;
    while (i !== end) {
      const elem = div.children[i] as HTMLElement;
      const item = elem?.children[0] as HTMLElement | undefined;
      const action = elem?.children[1] as HTMLElement | undefined;

      if (focusNext) {
        if (focusNext === 'item') {
          item?.focus();
        } else if (focusNext === 'action') {
          action?.focus();
        } else if (focusNext === 'elem') {
          elem.focus();
        }
        focusNext = undefined;
      } else {
        focusNext =
          document.activeElement === item
            ? 'item'
            : document.activeElement === action
            ? 'action'
            : document.activeElement === elem
            ? 'elem'
            : undefined;
      }

      i = inc(i);
    }
  }

  function focusHorizontal(div: HTMLElement, dir: 'left' | 'right') {
    for (let i = 0; i < div.children.length; i++) {
      const el = div.children[i] as HTMLElement;
      const item = el.children[0] as HTMLElement;
      const action = el.children[1] as HTMLElement;

      if (document.activeElement === item && dir === 'right') {
        action.focus();
      } else if (document.activeElement === action && dir === 'left') {
        item.focus();
      }
    }
  }

  function getFocusedIdx(div: HTMLElement) {
    let focusedIdx = -1;

    for (let i = 0; i < div.children.length; i++) {
      const el = div.children[i] as HTMLElement;
      const item = el.children[0] as HTMLElement;
      const action = el.children[1] as HTMLElement;

      if (
        document.activeElement === item ||
        document.activeElement === action ||
        document.activeElement === el
      ) {
        focusedIdx = i;
      }
    }

    return focusedIdx;
  }

  function focusSpecificIdx(div: HTMLElement, idx: number, type: 'item' | 'action' | 'elem') {
    const elem = div.children[idx] as HTMLElement;
    const item = elem?.children[0] as HTMLElement | undefined;
    const action = elem?.children[1] as HTMLElement | undefined;

    switch (type) {
      case 'item':
        item?.focus();
        break;
      case 'action':
        action?.focus();
        break;
      case 'elem':
        elem?.focus();
        break;
    }
  }

  function typeaheadEvtHandler(
    evt: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>
  ) {
    if (hasOwnProperty(evt, 'key')) {
      switch (evt.key) {
        case 'ArrowUp':
          if (itemsRef.current && inputRef.current && getFocusedIdx(itemsRef.current) === 0) {
            inputRef.current.focus();
          } else if (itemsHasFocus && itemsRef.current) {
            focusVertical(itemsRef.current, itemsRef.current.children.length - 1, -1, i => i - 1);
          }
          break;
        case 'ArrowDown':
          if ((badgeHasFocus || inputHasFocus) && itemsRef.current) {
            const secondEl = itemsRef.current.children[1] as HTMLElement;
            const secondElItem = secondEl?.children[0] as HTMLElement;
            secondElItem?.focus();
          } else if (itemsHasFocus && itemsRef.current) {
            focusVertical(itemsRef.current, 0, itemsRef.current.children.length, i => i + 1);
          }
          break;
        case 'ArrowLeft':
          if (inputHasFocus && search.length) {
            // do nothing
          } else if (inputHasFocus && typeaheadRef.current) {
            focusSpecificIdx(typeaheadRef.current, totalBadgesAmt - 1, 'elem');
          } else if (badgeHasFocus && typeaheadRef.current) {
            focusVertical(
              typeaheadRef.current,
              typeaheadRef.current.children.length,
              -1,
              i => i - 1
            );
          } else if (itemsHasFocus && itemsRef.current) {
            focusHorizontal(itemsRef.current, 'left');
          }
          break;
        case 'ArrowRight':
          if (inputHasFocus && search.length) {
            // do nothing
          } else if (badgeHasFocus && typeaheadRef.current) {
            if (getFocusedIdx(typeaheadRef.current) === totalBadgesAmt - 1 && inputRef.current) {
              inputRef.current.focus();
            } else {
              focusVertical(
                typeaheadRef.current,
                0,
                typeaheadRef.current.children.length,
                i => i + 1
              );
            }
          } else if (itemsHasFocus && itemsRef.current) {
            focusHorizontal(itemsRef.current, 'right');
          } else if (inputHasFocus && itemsRef.current) {
            const firstEl = itemsRef.current.children[0] as HTMLElement;
            const firstElItem = firstEl?.children[1] as HTMLElement;
            firstElItem?.focus();
          }
          break;
      }
    } else {
      // on click handler
      if (!noAutoFocus && inputRef && inputRef.current) inputRef.current.focus();
      if (onClick) onClick(evt);
    }
  }

  function inputEvtHandler(
    evt: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>
  ) {
    if (hasOwnProperty(evt, 'key')) {
      if (evt.key === 'Enter') {
        evt.stopPropagation();
        evt.preventDefault();

        if (internalOptions.options.length > 0) {
          onAddItem(internalOptions.options[0]);
        } else if (showNewItemOption) {
          onCreateItem();
        }
      }

      if (evt.key === 'Backspace' && search.length === 0 && typeaheadRef.current) {
        evt.stopPropagation();
        evt.preventDefault();
        focusSpecificIdx(typeaheadRef.current, totalBadgesAmt - 1, 'elem');
      }
    }
  }

  function clearItemsEvtHandler(
    evt: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>
  ) {
    if (hasOwnProperty(evt, 'key') && evt.key != 'Enter') {
      return; // we only care about enter key
    }

    evt.preventDefault();
    evt.stopPropagation();

    onClearItems();
  }

  function createItemEvtHandler(
    evt: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>
  ) {
    if (hasOwnProperty(evt, 'key') && evt.key != 'Enter') {
      return; // we only care about enter key
    }

    evt.preventDefault();
    evt.stopPropagation();

    onCreateItem();
  }

  function addItemEvtHandler(
    evt: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>,
    opt: BubbleTypeaheadOption<T>
  ) {
    if (noAutoFocus || (hasOwnProperty(evt, 'key') && evt.key != 'Enter')) {
      return; // we only care about enter key
    }

    evt.preventDefault();
    evt.stopPropagation();

    onAddItem(opt);
  }

  function removeItemEvtHandler(
    evt: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>,
    val: BubbleTypeaheadValue<T>
  ) {
    if (hasOwnProperty(evt, 'key') && evt.key != 'Enter' && evt.key != 'Backspace') {
      return; // we only care about enter or backspace key
    }

    evt.preventDefault();
    evt.stopPropagation();

    onRemoveItem(val);
  }

  function trashItemEvtHandler(
    evt: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>,
    opt: BubbleTypeaheadOption<T>
  ) {
    if (hasOwnProperty(evt, 'key') && evt.key != 'Enter') {
      return; // we only care about enter key
    }

    evt.preventDefault();
    evt.stopPropagation();

    onTrashItem(opt);
  }

  const classes = ['bubble-typeahead'];
  if (block) classes.push('full');
  if (appearance) classes.push(appearance);
  if (variant) classes.push(variant);
  if (propClassName) classes.push(propClassName);

  let style: React.CSSProperties = {};
  if (propStyle) style = { ...style, ...propStyle };

  const inputClasses = ['bt-input'];
  if (propInputClassName) inputClasses.push(propInputClassName);

  let inputStyle: React.CSSProperties = {};
  if (propInputStyle) inputStyle = { ...inputStyle, ...propInputStyle };

  const menuClasses = ['bubble-suggestions'];
  if (propMenuClassName) menuClasses.push(propMenuClassName);

  const suggestionClasses = ['suggestions-item', 's-tab'];
  if (propSuggestionClassName) suggestionClasses.push(propSuggestionClassName);

  const showNewItemOption =
    !internalOptions.hasPerfectMatch && allowNew && trimmedSearch.length > 0;
  const showNoSuggestionsInfo = !internalOptions.options.length;

  const noneValueDescription = readOnlyLabel ?? 'None';

  if (readOnly) {
    return (
      // FIXME: add way of passing down classes and styles from parent?
      <div {...rest} className="flex flex-wrap items-center -mr-1 -mb-1">
        {internalValue.map(intVal => (
          <BubbleBadge
            key={intVal.value.key}
            variant={intVal.option.variant}
            className="bt-badge b-tab mr-1 mb-1"
            color={
              !intVal.option.variant
                ? colorExtractor
                  ? colorExtractor(intVal.value.item)
                  : hashStringToColor(resolveLabel(intVal.value))
                : undefined
            }
          >
            <span className="bt-badge-text">{resolveLabel(intVal.value)}</span>
          </BubbleBadge>
        ))}
        {internalValue.length === 0 && (
          <span className="text-smoke italic mr-1 mb-1">{noneValueDescription}</span>
        )}
      </div>
    );
  }

  return (
    <div
      {...rest}
      ref={typeaheadRef}
      className={classes.join(' ')}
      style={style}
      onClick={typeaheadEvtHandler}
      onKeyDown={typeaheadEvtHandler}
      onFocus={calculateFocusStates}
      onBlur={calculateFocusStates}
    >
      {showClearAllBadge && (
        <BubbleBadge
          className="bt-badge b-tab"
          tabIndex={-1}
          clickable
          onMouseDown={clearItemsEvtHandler}
          onKeyDown={clearItemsEvtHandler}
          showIcon
        >
          <span className="bt-badge-text">Clear All</span>
        </BubbleBadge>
      )}
      {internalValue.map(intVal => (
        <BubbleBadge
          key={intVal.value.key}
          variant={intVal.option.variant}
          tabIndex={-1}
          className="bt-badge b-tab"
          clickable
          color={
            !intVal.option.variant
              ? colorExtractor
                ? colorExtractor(intVal.value.item)
                : hashStringToColor(resolveLabel(intVal.value))
              : undefined
          }
          onMouseDown={evt => removeItemEvtHandler(evt, intVal.value)}
          onKeyDown={evt => removeItemEvtHandler(evt, intVal.value)}
          showIcon={!intVal.value.readonly}
        >
          <span className="bt-badge-text">{resolveLabel(intVal.value)}</span>
        </BubbleBadge>
      ))}
      <BubbleInput
        inputRef={inputRef}
        value={trimmedSearch}
        type="text"
        appearance="bare"
        placeholder={placeholder}
        className="bt-input-container"
        inputClassName={inputClasses.join(' ')}
        style={inputStyle}
        onValue={setSearch}
        onKeyDown={inputEvtHandler}
      />
      {(alwaysShowSuggestions || showSuggestions) && (
        <div ref={itemsRef} className={menuClasses.join(' ')}>
          {internalOptions.options.map((intOpt, i) => (
            <div
              className={`suggestions-option ${i === 0 && inputHasFocus ? 'active' : ''}`}
              key={intOpt.key}
            >
              <div
                className={suggestionClasses.join(' ')}
                tabIndex={-1}
                onMouseDown={evt => addItemEvtHandler(evt, intOpt)}
                onKeyDown={evt => addItemEvtHandler(evt, intOpt)}
              >
                {suggestionsRenderFn
                  ? suggestionsRenderFn(resolveLabel(intOpt), intOpt)
                  : resolveLabel(intOpt)}
              </div>
              {props.onTrash && (
                <div
                  className="suggestions-action s-tab"
                  tabIndex={-1}
                  onMouseDown={evt => trashItemEvtHandler(evt, intOpt)}
                  onKeyDown={evt => trashItemEvtHandler(evt, intOpt)}
                >
                  <FontAwesomeIcon icon="trash-alt" />
                </div>
              )}
            </div>
          ))}
          {showNoSuggestionsInfo && (
            <div className="suggestions-empty">{suggestionsText || 'No suggestions to show'}</div>
          )}
          {showNewItemOption && (
            <div
              className={`suggestions-new-container ${
                internalOptions.options.length === 0 && inputHasFocus ? 'active' : ''
              }`}
            >
              <div
                tabIndex={-1}
                className="suggestions-new s-tab"
                onMouseDown={createItemEvtHandler}
                onKeyDown={createItemEvtHandler}
              >
                <span className="new-label">Add new item:</span> {trimmedSearch}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default BubbleTypeahead;
